<template>
  <div class="border rounded p-1 custom-editor">
    <div v-if="editor" class="toolbar d-flex flex-wrap">

      <!-- Bold -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('bold'), disabled: !editor.can().chain().focus().toggleBold().run() }"
          @click="editor.chain().focus().toggleBold().run()"
          title="Bold (Ctrl+B)"
      >
        <i class="fas fa-bold"></i>
      </span>

      <!-- Italic -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('italic'), disabled: !editor.can().chain().focus().toggleItalic().run() }"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Italic (Ctrl+I)"
      >
        <i class="fas fa-italic"></i>
      </span>

      <!-- Strikethrough -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('strike'), disabled: !editor.can().chain().focus().toggleStrike().run() }"
          @click="editor.chain().focus().toggleStrike().run()"
          title="Strikethrough"
      >
        <i class="fas fa-strikethrough"></i>
      </span>

      <!-- Inline Code -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('code'), disabled: !editor.can().chain().focus().toggleCode().run() }"
          @click="editor.chain().focus().toggleCode().run()"
          title="Inline Code"
      >
        <i class="fas fa-code"></i>
      </span>

      <!-- Clear Marks -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().unsetAllMarks().run()"
          title="Clear Marks"
      >
        <i class="fas fa-eraser"></i>
      </span>

      <!-- Clear Nodes -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().clearNodes().run()"
          title="Clear Nodes"
      >
        <i class="fas fa-trash-alt"></i>
      </span>

      <!-- Paragraph -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('paragraph'), disabled: !editor.can().chain().focus().setParagraph().run() }"
          @click="editor.chain().focus().setParagraph().run()"
          title="Paragraph"
      >
        <i class="fas fa-paragraph"></i>
      </span>

      <!-- Headings -->
      <span
          v-for="level in [1, 2, 3, 4, 5, 6]"
          :key="level"
          class="toolbar-icon"
          :class="{ active: editor.isActive('heading', { level }) }"
          @click="editor.chain().focus().toggleHeading({ level }).run()"
          :title="`Heading ${level}`"
      >
        H{{ level }}
      </span>

      <!-- Bullet List -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Bullet List"
      >
        <i class="fas fa-list-ul"></i>
      </span>

      <!-- Ordered List -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Ordered List"
      >
        <i class="fas fa-list-ol"></i>
      </span>

      <!-- Code Block -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('codeBlock') }"
          @click="editor.chain().focus().toggleCodeBlock().run()"
          title="Code Block"
      >
        <i class="fas fa-file-code"></i>
      </span>

      <!-- Blockquote -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
          title="Blockquote"
      >
        <i class="fas fa-quote-right"></i>
      </span>

      <!-- Align Left -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive({ textAlign: 'left' }) }"
          @click="editor.chain().focus().setTextAlign('left').run()"
          title="Align Left"
      >
        <i class="fas fa-align-left"></i>
      </span>

      <!-- Align Center -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().setTextAlign('center').run()"
          title="Align Center"
      >
        <i class="fas fa-align-center"></i>
      </span>

      <!-- Align Right -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().setTextAlign('right').run()"
          title="Align Right"
      >
        <i class="fas fa-align-right"></i>
      </span>

      <!-- Justify -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive({ textAlign: 'justify' }) }"
          @click="editor.chain().focus().setTextAlign('justify').run()"
          title="Justify"
      >
        <i class="fas fa-align-justify"></i>
      </span>

      <!-- Horizontal Rule -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().setHorizontalRule().run()"
          title="Horizontal Rule"
      >
        <i class="fas fa-minus"></i>
      </span>

      <!-- Hard Break -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().setHardBreak().run()"
          title="Hard Break"
      >
        <i class="fas fa-level-down-alt"></i>
      </span>

      <!-- Insert Table -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
          title="Insert Table"
      >
        <i class="fas fa-table"></i>
      </span>

      <!-- Add Column -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().addColumnAfter() }"
          @click="editor.chain().focus().addColumnAfter().run()"
          title="Add Column"
      >
        <i class="fas fa-columns"></i>
      </span>

      <!-- Delete Column -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().deleteColumn() }"
          @click="editor.chain().focus().deleteColumn().run()"
          title="Delete Column"
      >
        <i class="fas fa-times-circle"></i>
      </span>

      <!-- Add Row -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().addRowAfter() }"
          @click="editor.chain().focus().addRowAfter().run()"
          title="Add Row"
      >
        <i class="fas fa-plus-square"></i>
      </span>

      <!-- Delete Row -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().deleteRow() }"
          @click="editor.chain().focus().deleteRow().run()"
          title="Delete Row"
      >
        <i class="fas fa-minus-square"></i>
      </span>

      <!-- Merge or Split Cells -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().mergeOrSplit() }"
          @click="editor.chain().focus().mergeOrSplit().run()"
          title="Merge or Split Cells"
      >
        <i class="fas fa-object-group"></i>
      </span>

      <!-- Delete Table -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().deleteTable() }"
          @click="editor.chain().focus().deleteTable().run()"
          title="Delete Table"
      >
        <i class="fas fa-trash"></i>
      </span>

      <!-- Line Height -->
      <select
          class="toolbar-select"
          :value="currentLineHeight"
          @change="applyLineHeight($event.target.value)"
          title="Line Height"
      >
        <option value="">Spacing</option>
        <option v-for="opt in lineHeights" :key="opt" :value="opt">{{ opt }}</option>
      </select>

      <!-- Undo -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().chain().focus().undo().run() }"
          @click="editor.chain().focus().undo().run()"
          title="Undo (Ctrl+Z)"
      >
        <i class="fas fa-undo"></i>
      </span>

      <!-- Redo -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().chain().focus().redo().run() }"
          @click="editor.chain().focus().redo().run()"
          title="Redo (Ctrl+Y)"
      >
        <i class="fas fa-redo"></i>
      </span>
    </div>
    <hr />

    <EditorContent :editor="editor" class="border rounded" />
  </div>
</template>

<script setup>
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import {
  EditorContent,
  useEditor,
} from '@tiptap/vue-3'
import {computed, onBeforeUnmount, watch} from "vue";
import LineHeight from './tiptap/lineHeight'

const props  = defineProps({
  content: {
    type:String,
    default: ''
  },
});

const lineHeights = ['0.25', '0.5', '1', '1.15', '1.5', '2', '2.5', '3'];

const editor = useEditor({
  content: props.content,
  extensions: [
    StarterKit,
    TextAlign.configure({
      types: [
        'heading',
        'paragraph',
      ],
    }),
    Placeholder.configure({ placeholder: 'Write something here...' }),
    Underline,
    LineHeight,
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
  ],
    onUpdate({ editor }) {
     emit('update:content', editor.getHTML())
  }
})

const emit = defineEmits(['update:content'])

const currentLineHeight = computed(() => {
  if (! editor.value) return ''
  return editor.value.getAttributes('paragraph').lineHeight || ''
})

const applyLineHeight = (value) => {
  if (! editor.value) return
  if (! value) {
    editor.value.chain().focus().unsetLineHeight().run()
    return
  }
  editor.value.chain().focus().setLineHeight(value).run()
}

watch(
    () => props.content,
    (newContent) => {
      if (editor.value && newContent !== editor.value.getHTML()) {
        editor.value.commands.setContent(newContent)
      }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
  editor.value ? editor.value.destroy() : ''
})

defineExpose({
  getHTML: () => editor.value ? editor.value.getHTML() : '',
})
</script>

<style>
.custom-editor .ProseMirror {
  min-height: 100px;
  outline: none;
  overflow-y: auto;
  padding: 0.5rem;
}
.custom-editor .ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0.5rem 0;
  overflow: hidden;
}

.custom-editor .ProseMirror table td,
.custom-editor .ProseMirror table th {
  border: 1px solid #ced4da;
  padding: 4px 6px;
  vertical-align: top;
  position: relative;
  min-width: 1em;
}

.custom-editor .ProseMirror table th {
  background-color: #f3f2f7;
  font-weight: bold;
  text-align: left;
}

.custom-editor .ProseMirror table .selectedCell:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(25, 118, 210, 0.1);
  pointer-events: none;
}

.custom-editor .ProseMirror table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 4px;
  background-color: #1976d2;
  pointer-events: none;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.5rem;
}

.custom-editor hr {
  margin: 0.5rem 0;
}

.toolbar-select {
  height: 30px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #444;
  padding: 0 4px;
  cursor: pointer;
}

.toolbar-icon {
  cursor: pointer;
  font-size: 1.2rem;
  color: #444;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  transition: color 0.2s ease;
  border-radius: 4px;
}

.toolbar-icon:hover {
  color: #1976d2;
  background-color: rgba(25, 118, 210, 0.1);
}

.toolbar-icon.active {
  color: white;
  background-color: #1976d2;
}

.toolbar-icon.disabled {
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}
</style>